import { Box, CircularProgress, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Collection, ProductOnUser, UIBlockElement } from 'common-ts/dist/models/Product';
import React, { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link as RouterLink } from 'react-router-dom';
import { useAppSelector } from '../reduxhooks';
import { CategoryPageContext } from './CategoryPageContext';
import { RenderBlockElement } from './PageComponentElementBlocks';

// BlockCollectionProducts: the product block of a category page scaffold.
// Resolves its Koleksi from CategoryPageContext (or an explicit override)
// and renders them in one of two tenant-selectable modes:
//   "efficient" — the Koleksi flattened into one deduped Shopee-style grid
//                 (sort tab bar + dense square-image cards).
//   "discovery" — each Koleksi's discoveryElements feed, in category order,
//                 with infinite scroll internal to this block (so the rest
//                 of the scaffold — heroes, copy — renders eagerly).
export interface BlockCollectionProductsData {
  mode?: 'discovery' | 'efficient';
  // Explicit Koleksi override; defaults to the page's category context.
  collectionHandles?: string[];
  // Efficient-grid styling overrides.
  containerMargin?: string;
  productPadding?: string;
}

const ELEMENTS_PER_SCROLL_CHUNK = 3;

// Kumo (Cloudflare-inspired) tokens, matching the prodpanel restyle.
// The font must be set explicitly: the storefront has no global body font
// (no CssBaseline), so bare divs inherit the browser's Times serif.
export const KUMO_FONT = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const K = {
  text: '#0A0A0A',
  secondary: '#525252',
  muted: '#737373',
  hairline: '#E9E9E9',
  tint: '#F5F5F5',
  primary: '#056DFF',
  dangerTintBg: '#FFE2E2',
  dangerTintText: '#9F0712',
};

// Shopee-style sort bar: Populer (the merchant-curated Koleksi order) /
// Terbaru / Terlaris / Harga, where tapping Harga toggles asc ⇄ desc.
type SortTab = 'populer' | 'terbaru' | 'terlaris' | 'harga';

// Selling price: the discounted price when a discount exists.
function effectivePrice(product: ProductOnUser): number {
  return product.discountedPrice !== -1 ? product.discountedPrice : product.originalPrice;
}

function priceText(value: number): string {
  return 'Rp ' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Shopee-style compact sold count: 2.345 → "2RB+ terjual".
function soldText(totalSold: number): string {
  if (totalSold >= 1000) return Math.floor(totalSold / 1000) + 'RB+ terjual';
  return totalSold + ' terjual';
}

const TITLE_CLAMP_STYLE: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as any,
  overflow: 'hidden',
  fontSize: 13,
  lineHeight: 1.35,
  minHeight: '2.7em',
  color: K.text,
};

function EfficientProductCard({ product }: { product: ProductOnUser }) {
  const hasDiscount =
    product.discountedPrice !== -1 && product.discountedPrice < product.originalPrice;
  const price = effectivePrice(product);
  const discountPct = hasDiscount
    ? Math.round((1 - product.discountedPrice / product.originalPrice) * 100)
    : 0;
  const sold = product.totalSold ?? 0;

  return (
    <RouterLink to={'/shop/product/' + product.productId} style={{ textDecoration: 'none' }}>
      <Box style={{
        background: '#ffffff',
        border: `1px solid ${K.hairline}`,
        borderRadius: 8,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box style={{ position: 'relative', width: '100%', paddingBottom: '100%' }}>
          <img
            src={product.displayImage}
            alt={product.name}
            loading="lazy"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: product.isSoldOut ? 0.45 : 1,
            }}
          />
          {product.isSoldOut &&
            <Box style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(10, 10, 10, 0.72)',
              color: '#ffffff',
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 999,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}>
              Stok Habis
            </Box>}
        </Box>
        <Box style={{ padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box style={TITLE_CLAMP_STYLE}>{product.name}</Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Box style={{ fontSize: 16, fontWeight: 600, color: K.text }}>
              {priceText(price)}
            </Box>
            {hasDiscount &&
              <Box style={{
                fontSize: 11,
                fontWeight: 600,
                color: K.dangerTintText,
                background: K.dangerTintBg,
                borderRadius: 999,
                padding: '2px 7px',
              }}>
                -{discountPct}%
              </Box>}
          </Box>
          {sold > 0 &&
            <Box style={{ fontSize: 12, color: K.muted }}>{soldText(sold)}</Box>}
        </Box>
      </Box>
    </RouterLink>
  );
}

// The Shopee shop-page sort bar, rebuilt with Kumo styling: white bar with
// hairline border, active tab in the primary blue, "Harga" with stacked
// asc/desc carets.
function EfficientSortBar({ tab, priceAsc, onSelect }: {
  tab: SortTab;
  priceAsc: boolean;
  onSelect: (tab: SortTab) => void;
}) {
  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    textAlign: 'center',
    padding: '11px 0',
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: K.secondary,
    cursor: 'pointer',
    userSelect: 'none',
  });
  const caretStyle = (active: boolean): React.CSSProperties => ({
    display: 'block',
    fontSize: 7,
    lineHeight: '7px',
    color: '#B0B0B0',
  });

  const textTabs: { key: SortTab; label: string }[] = [
    { key: 'populer', label: 'Populer' },
    { key: 'terbaru', label: 'Terbaru' },
    { key: 'terlaris', label: 'Terlaris' },
  ];

  return (
    <Box style={{
      display: 'flex',
      alignItems: 'stretch',
      background: '#ffffff',
      border: `1px solid ${K.hairline}`,
      borderRadius: 10,
      marginBottom: 10,
    }}>
      {textTabs.map((entry, i) => (
        <React.Fragment key={entry.key}>
          {i > 0 && <Box style={{ borderLeft: `1px solid ${K.hairline}`, margin: '10px 0' }} />}
          <Box style={tabStyle(tab === entry.key)} onClick={() => onSelect(entry.key)}
            id={'tb-sort-' + entry.key}>
            {entry.label}
          </Box>
        </React.Fragment>
      ))}
      <Box style={{ borderLeft: `1px solid ${K.hairline}`, margin: '10px 0' }} />
      <Box
        style={{ ...tabStyle(tab === 'harga'), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
        onClick={() => onSelect('harga')}
        id="tb-sort-harga"
      >
        Harga
        <span>
          <span style={caretStyle(tab === 'harga' && priceAsc)}>▲</span>
          <span style={caretStyle(tab === 'harga' && !priceAsc)}>▼</span>
        </span>
      </Box>
    </Box>
  );
}

interface Props {
  elementContentJson: any;
}

export default function BlockCollectionProducts({ elementContentJson }: Props) {
  const elementContent: BlockCollectionProductsData = elementContentJson ?? {};

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const context = useContext(CategoryPageContext);
  const collectionsDictionary = useAppSelector(state => state.productsList.collectionsDictionary);
  const productsDictionary = useAppSelector(state => state.productsList.productsDictionary);
  const efficientProductsOrder = useAppSelector(state => state.productsList.efficientProductsOrder);
  const storeProfile = useAppSelector(state => state.storeProfile.profile);

  // Efficient-grid sort state.
  const [sortTab, setSortTab] = useState<SortTab>('populer');
  const [priceAsc, setPriceAsc] = useState(true);

  const handles = elementContent.collectionHandles ?? context?.collectionHandles ?? [];
  // Platform default is 'efficient'; 'discovery' must be opted into.
  const storeDefaultMode =
    storeProfile?.config?.categoryDefaultMode === 'discovery' ? 'discovery' : 'efficient';
  const mode = elementContent.mode ?? storeDefaultMode;

  // Sold-out products stay VISIBLE (greyed card + "Stok Habis" badge), so
  // collections are no longer dropped when nothing in them is purchasable.
  // A fully out-of-stock catalog previously rendered a bare sort bar with no
  // explanation — indistinguishable from a broken page (havasu-id incident).
  const collections = handles
    .map((handle) => collectionsDictionary[handle])
    .filter((collection): collection is Collection => !!collection);

  // ---- Discovery feed: chunked infinite scroll over Koleksi sections ----

  const feedElements: JSX.Element[] = [];
  if (mode === 'discovery') {
    for (const collection of collections) {
      for (const blockData of collection.discoveryListOfElements as UIBlockElement[]) {
        const code = isDesktop ? blockData.elementDesktopCode : blockData.elementMobileCode;
        const contentJson = isDesktop
          ? blockData.elementDesktopContentJson
          : blockData.elementMobileContentJson;
        if (!code || code === 'Empty') continue;
        feedElements.push(
          <RenderBlockElement
            key={collection.collectionId + '-' + feedElements.length}
            code={code}
            contentJson={contentJson}
          />
        );
      }
    }
  }
  const chunkCount = Math.ceil(feedElements.length / ELEMENTS_PER_SCROLL_CHUNK);

  const [visibleChunks, setVisibleChunks] = useState(2);
  useEffect(() => {
    setVisibleChunks(2);
  }, [handles.join(','), isDesktop, mode]); // eslint-disable-line

  if (mode === 'efficient') {
    // Merchant-curated order across the Koleksi, deduped — this IS the
    // "Populer" ordering.
    const seen: { [productId: string]: boolean } = {};
    const curatedIds: string[] = [];
    for (const collection of collections) {
      for (const productId of collection.efficientOrderOfItems) {
        if (seen[productId] || !productsDictionary[productId]) continue;
        seen[productId] = true;
        curatedIds.push(productId);
      }
    }

    // "Terbaru" is newest arrival: each product's index in the catalog
    // order the backend returns (publishedAt desc).
    const newestIndex: { [productId: string]: number } = {};
    efficientProductsOrder.forEach((productId, i) => { newestIndex[productId] = i; });

    const comparators: { [key: string]: (a: ProductOnUser, b: ProductOnUser) => number } = {
      // Populer keeps the merchant's curated order, so its comparator is a
      // no-op — the sold-out tiebreak below is the only thing that moves it.
      populer: () => 0,
      terbaru: (a, b) =>
        (newestIndex[a.productId] ?? Number.MAX_SAFE_INTEGER)
        - (newestIndex[b.productId] ?? Number.MAX_SAFE_INTEGER),
      terlaris: (a, b) => (b.totalSold ?? 0) - (a.totalSold ?? 0),
      harga: (a, b) => priceAsc
        ? effectivePrice(a) - effectivePrice(b)
        : effectivePrice(b) - effectivePrice(a),
    };
    // Sold-out products stay visible but always sink below in-stock ones, in
    // every mode — a shopper scanning the grid should hit buyable items first.
    // Array.prototype.sort is stable (ES2019+), so equal-ranked products keep
    // their curated order within each group.
    const productIds = [...curatedIds].sort((idA, idB) => {
      const a = productsDictionary[idA];
      const b = productsDictionary[idB];
      if (!!a.isSoldOut !== !!b.isSoldOut) return a.isSoldOut ? 1 : -1;
      return comparators[sortTab](a, b);
    });

    const columns = isLg ? 5 : isMd ? 4 : isDesktop ? 3 : 2;

    const handleSortSelect = (tab: SortTab) => {
      if (tab === 'harga') {
        // Shopee behavior: first tap sorts ascending, tapping again flips.
        setPriceAsc(sortTab === 'harga' ? !priceAsc : true);
      }
      setSortTab(tab);
    };

    return (
      <Box style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box style={{
          width: isDesktop ? '81%' : '92%',
          margin: elementContent.containerMargin
            ?? (isDesktop ? '25px auto 20px' : '15px auto 10px'),
          fontFamily: KUMO_FONT,
        }}>
          <EfficientSortBar tab={sortTab} priceAsc={priceAsc} onSelect={handleSortSelect} />
          <Box style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: isDesktop ? 12 : 8,
          }}>
            {productIds.map((productId) => (
              <EfficientProductCard key={productId} product={productsDictionary[productId]} />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  const visibleElements = feedElements.slice(0, visibleChunks * ELEMENTS_PER_SCROLL_CHUNK);

  return (
    <InfiniteScroll
      dataLength={visibleElements.length}
      next={() => setVisibleChunks((n) => n + 1)}
      hasMore={visibleChunks < chunkCount}
      loader={
        <div style={{ width: '100vw', minHeight: '160px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </div>
      }
      endMessage={<p style={{ textAlign: 'center' }}></p>}
      scrollThreshold="800px"
    >
      {visibleElements}
    </InfiniteScroll>
  );
}
